import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const CoWorkingSpaces = () => {
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Organic gradient on the right with animations */}
      <div className="absolute -top-20 -right-20 w-[800px] h-[800px]">
        <div 
          className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-500/40 via-indigo-500/30 to-blue-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatSlow 10s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute top-60 right-40 w-[500px] h-[500px] bg-gradient-to-tl from-indigo-400/30 via-purple-400/20 to-violet-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftRight 12s ease-in-out infinite'
          }}
        ></div>
      </div>

      {/* Organic gradient on the bottom left with animations */}
      <div className="absolute -bottom-40 -left-40 w-[900px] h-[900px]">
        <div 
          className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-br from-indigo-500/35 via-purple-500/25 to-violet-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatLeft 14s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-40 w-[600px] h-[600px] bg-gradient-to-tl from-purple-400/30 via-indigo-400/20 to-blue-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftLeft 8s ease-in-out infinite'
          }}
        ></div>
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-left">
            <div className="mb-8">
              <span className="text-white text-sm font-semibold tracking-wider uppercase opacity-90">FOCUS AREAS</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-white leading-tight">
              Co-working Spaces
            </h1>
            <p className="text-xl text-gray-300 mt-8 max-w-3xl font-light leading-relaxed">
              CRADLE, our premium co-working chain, is fully owned and managed by VentureCorp (Pvt) Ltd. We provide a professional and supportive environment tailored for startups and growing businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight mb-8">
                International startup hub with unique facilities and community
              </h2>
            </div>
            {/* Right column - Content */}
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                Our facilities are trusted by a significant number of <span className="text-purple-300 font-medium">US and Australia-based start-ups</span>, making CRADLE a vibrant hub for innovation and collaboration. We offer more than just workspace - our co-working spaces are designed to foster creativity, networking, and productivity.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Start-ups benefit from <span className="text-purple-300 font-medium">modern workstations and meeting rooms</span>, a <span className="text-purple-300 font-medium">collaborative and inspiring environment</span>, and opportunities to connect with like-minded entrepreneurs from around the world.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                As a fully owned and managed VentureCorp facility, CRADLE provides the perfect blend of professional infrastructure and entrepreneurial community support for growing businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto">
            {/* Services Grid */}
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 text-left">
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Managed by VentureCorp</h3>
              <p className="text-gray-300 font-light">CRADLE, our premium co-working chain, is fully owned and managed by VentureCorp (Pvt) Ltd, providing professional and supportive environment tailored for startups.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Fully owned and managed facility
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Professional business environment
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Tailored for startups and growing businesses
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">International Start-Up Hub</h3>
              <p className="text-gray-300 font-light">Our facilities are trusted by a significant number of US and Australia-based start-ups, making CRADLE a vibrant hub for innovation and collaboration.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Trusted by US and Australia-based startups
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Vibrant hub for innovation
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  International collaboration opportunities
                </li>
              </ul>
              </div>
              <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Unique Facilities & Community</h3>
              <p className="text-gray-300 font-light">We offer more than just workspace - our co-working spaces are designed to foster creativity, networking, and productivity with modern facilities.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Modern workstations and meeting rooms
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Collaborative and inspiring environment
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Connect with like-minded entrepreneurs
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Workspace Types Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">CRADLE CO-WORKING SPACES</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Premium co-working facilities designed for international startups
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Modern Workstations</h3>
                <p className="text-gray-300 font-light mb-6">State-of-the-art workstations equipped with high-speed internet, ergonomic furniture, and all the tools needed for productive work. Perfect for individual entrepreneurs and small teams.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    High-speed internet connectivity
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Ergonomic furniture and equipment
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Personal storage solutions
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Professional Meeting Rooms</h3>
                <p className="text-gray-300 font-light mb-6">Fully equipped meeting rooms with modern presentation technology, perfect for client meetings, team collaborations, and important business discussions.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    HD displays and projectors
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Video conferencing capabilities
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Flexible seating arrangements
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">Collaborative Environment</h3>
                <p className="text-gray-300 font-light mb-6">Designed to foster creativity and networking, our spaces encourage collaboration between like-minded entrepreneurs from around the world.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Open and inspiring design
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Networking opportunities
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Community events and workshops
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-800/20 p-8 rounded-2xl border border-slate-700/30">
                <h3 className="text-2xl font-light text-white mb-4">International Community</h3>
                <p className="text-gray-300 font-light mb-6">Join a vibrant community of US and Australia-based startups, creating opportunities for international collaboration and knowledge sharing.</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Global startup network
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    Cross-cultural collaboration
                  </li>
                  <li className="flex items-center">
                    <span className="text-purple-300 mr-3">✓</span>
                    International business opportunities
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-16 font-AfacadFluxLight">
            Why Choose VentureCorp?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Global Experience, Local Expertise</h3>
              <p className="text-gray-300">Trusted by startups across the US, UK, Australia, India, UAE, and Sri Lanka</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">All-in-One Business Solution</h3>
              <p className="text-gray-300">HR, Payroll, Accounting, Legal, and Digital Marketing under one roof</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Scalable Services</h3>
              <p className="text-gray-300">Whether you're a solo founder or a 100+ employee company, our services grow with you</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">End-to-End Support</h3>
              <p className="text-gray-300">From company formation to daily operations, we've got your back</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Events Section */}
      {/* <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">COMMUNITY & EVENTS</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              Building connections and fostering collaboration through events
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Networking Events</h3>
              <p className="text-gray-300 text-sm">Monthly networking mixers and industry meetups</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Workshops</h3>
              <p className="text-gray-300 text-sm">Skill-building sessions and industry workshops</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2v2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 00-2 2v2" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Demo Days</h3>
              <p className="text-gray-300 text-sm">Startup pitch events and product demonstrations</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Lunch & Learns</h3>
              <p className="text-gray-300 text-sm">Educational sessions during lunch hours</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Hackathons</h3>
              <p className="text-gray-300 text-sm">Innovation challenges and coding competitions</p>
            </div>
            
            <div className="bg-slate-800/20 p-6 rounded-2xl border border-slate-700/30 text-center hover:bg-slate-800/30 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-light text-white mb-2">Wellness Programs</h3>
              <p className="text-gray-300 text-sm">Yoga, meditation, and wellness activities</p>
            </div>
          </div>
        </div>
      </section> */}


      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6 font-AfacadFluxLight">
            Ready to Find Your Perfect Workspace?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join our community of entrepreneurs and professionals in a space designed for success
          </p>
          <div className="flex justify-center">
            <Link to="/contact" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl font-AfacadFluxLight uppercase tracking-wide">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>


      {/* CSS Animations */}
      <style jsx>{`
        @keyframes floatSlow {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(80px, -100px) rotate(3deg);
          }
          66% {
            transform: translate(-60px, 60px) rotate(-2deg);
          }
        }

        @keyframes driftRight {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(40px, -60px) scale(1.1);
          }
          50% {
            transform: translate(-32px, 32px) scale(0.88);
          }
          75% {
            transform: translate(48px, 40px) scale(1.06);
          }
        }

        @keyframes floatLeft {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-72px, 88px) rotate(-3deg);
          }
          66% {
            transform: translate(100px, -72px) rotate(2.5deg);
          }
        }

        @keyframes driftLeft {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(-48px, 64px) scale(1.12);
          }
          50% {
            transform: translate(32px, -40px) scale(0.84);
          }
          75% {
            transform: translate(-56px, -32px) scale(1.08);
          }
        }
      `}</style>
    </div>
  );
};

export default CoWorkingSpaces; 